import React, { useCallback, useState } from 'react';
import { format } from 'date-fns';
import { useMutation, useQuery, useQueryCache } from 'react-query';
import { QueryKey } from 'api/queryKey';
import { Button, message } from 'antd';
import {
  BusinessPartnersAPI,
  TransferProfileOwnerDTO,
} from 'api/BusinessPartnersAPI';
import { atom, useAtom } from 'jotai';
import { commonPaginationConfig, PageParams } from 'models/page';
import { purge } from 'lib/ramda/purge';
import { BusinessPartnerFilters } from 'pages/SuperAdminModule/BusinessPartnersManagement/BusinessPartnersFilers';
import Column from 'antd/lib/table/Column';
import Modal, { ModalProps } from 'antd/lib/modal';
import styled from 'styled-components';
import { ProfileOwnerTransferRadioOptions } from 'pages/BusinessPartnerManagement/BusinessPartnerProfileOwnersManagement/BusinessPartnerProfileOwnersPage/ProfileOwnerTransferRadioOptions';
import { ProfileOwnerDTO, ProfileOwnerTransferOption } from 'models/user';
import { useEnsureContext } from 'common/hooks';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { FormattedMessage } from 'react-intl';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { NumberedTable } from 'common/components/NumberedTable';

interface ProfileOwnerTransferModalProps extends ModalProps {
  hideModal: () => void;
  user: ProfileOwnerDTO;
}

const businessPartnersFiltersAtom = atom<PageParams>({ page: 1, pageSize: 5 });

const StyledModal = styled(Modal)`
  top: 50px;
  width: 90vw !important;

  .ant-modal-body {
    height: 70vh;
    overflow-y: auto;
    overflow-x: hidden;
  }
`;

export const ProfileOwnerTransferModal: React.FC<ProfileOwnerTransferModalProps> = ({
  hideModal,
  user,
  ...modalProps
}) => {
  const [pageMetaData, setPageMetaData] = useAtom(businessPartnersFiltersAtom);
  const [filters, setFilters] = useState({});
  const queryCache = useQueryCache();

  const bpContext = useEnsureContext(BusinessPartnerContext);
  const [selectedPartnerId, setSelectedPartnerId] = useState<number>();
  const [selectedTransferOption, setSelectedTransferOption] = useState<
    ProfileOwnerTransferOption
  >();

  const { isFetching, data } = useQuery(
    [QueryKey.BusinessPartnersListWithoutContextPartner, pageMetaData, filters],
    () =>
      BusinessPartnersAPI.fetch({
        ...pageMetaData,
        ...filters,
      }),
    {
      keepPreviousData: true,
    }
  );

  const [mutate, { isLoading }] = useMutation(
    BusinessPartnersAPI.transferProfileOwnerToOtherPartner
  );

  const paginationConfig = {
    ...commonPaginationConfig,
    current: pageMetaData?.page,
    pageSize: data?.size,
    total: data?.totalElements,
    onChange: (page: number, pageSize?: number) =>
      setPageMetaData((prev) => ({
        ...prev,
        page,
        ...(pageSize && { pageSize }),
      })),
  };

  const invalidateProfileOwners = useCallback(
    () =>
      queryCache.invalidateQueries(QueryKey.BusinessPartnerProfileOwnersList),
    [queryCache]
  );

  const intlMessage = useFormatMessage();

  const handleSubmit = () => {
    if (selectedPartnerId && selectedTransferOption) {
      const dto: TransferProfileOwnerDTO = {
        userId: user.userId!,
        newPartnerId: selectedPartnerId,
        shiftOption: selectedTransferOption,
      };

      mutate(
        {
          businessPartnerId: user.partnerDetails
            ? user.partnerDetails.id
            : bpContext.id,
          dto,
        },
        {
          onSuccess: () => {
            message.success(
              intlMessage(
                { id: 'familyUsers.transfer.success' },
                {
                  firstName: user.firstName,
                  lastName: user.lastName,
                }
              )
            );
            invalidateProfileOwners();
            hideModal();
          },
        }
      );
    }
  };

  return (
    <>
      <StyledModal
        title={intlMessage(
          { id: 'familyUsers.transferToBP' },
          {
            firstName: user.firstName,
            lastName: user.lastName,
          }
        )}
        {...modalProps}
        footer={[
          <Button
            id="button-cancel-transfer-profile-owner"
            key="cancel"
            loading={false}
            onClick={hideModal}
          >
            <FormattedMessage id="cancel" />
          </Button>,
          <Button
            id="button-submit-transfer-profile-owner"
            key="submit"
            type="primary"
            loading={isLoading}
            onClick={handleSubmit}
            disabled={!selectedPartnerId || !selectedTransferOption}
          >
            <FormattedMessage id="submit" />
          </Button>,
        ]}
        onCancel={hideModal}
      >
        <FormattedMessage id="businessPartners.select" tagName="h3" />
        <BusinessPartnerFilters
          style={{ margin: '1rem 0' }}
          onSubmit={(values) => {
            setPageMetaData((pageMetaData) => ({ ...pageMetaData, page: 1 }));
            setFilters(purge(values));
          }}
        />
        <NumberedTable
          rowKey="id"
          loading={isFetching}
          dataSource={data?.content}
          pagination={paginationConfig}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedPartnerId ? [selectedPartnerId] : [],
            onSelect: (record) => setSelectedPartnerId(record.id),
          }}
          onRow={(record) => ({
            onClick: () => {
              setSelectedPartnerId(record.id);
            },
          })}
          rowClassName="ant-table-row-interactive"
          scroll={{ x: 'auto' }}
        >
          <Column title={intlMessage({ id: 'name' })} dataIndex="name" />
          <Column
            title={intlMessage({ id: 'identifier' })}
            dataIndex="uniqueNumber"
          />
          <Column
            title={intlMessage({ id: 'registrationDate' })}
            dataIndex="createDate"
            align="center"
            render={(value) => format(value, 'dd.MM.yyyy')}
          />
        </NumberedTable>
        <FormattedMessage id="familyUsers.transfer.selectOption" tagName="h3" />
        <ProfileOwnerTransferRadioOptions
          setOption={setSelectedTransferOption}
          option={selectedTransferOption}
        />
      </StyledModal>
    </>
  );
};
